/**
 * Video Content Manager - Moon Moon Agency
 * Section MM Video Content : toggle mute de la vidéo autoplay + modal de lecture plein écran.
 */

/*********************************************************************************************
 * 🎬 VIDEO CONTENT MANAGER
 */
const MMVideoContentManager = {
	config: {
		selectors: {
			root: '[data-mm-video-content]',
			mute: '[data-mm-video-mute]',
			video: '.mm-video-content__video',
			open: '[data-mm-video-open]',
			modal: '[data-mm-video-modal]',
			close: '[data-mm-video-close]',
			fullscreen: '.mm-video-content__modal-video',
			focusable: 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
		},
	},

	_activeModal: null,
	_trigger: null,

	_bound: false,

	init() {
		if (this._bound) return;
		if (!document.querySelector(this.config.selectors.root)) return;

		this._bindEvents();
		this._bound = true;
		window.log?.('info', '🎬 [VIDEO_CONTENT]', 'Initialized');
	},

	_bindEvents() {
		document.addEventListener('click', (e) => {
			if (e.target.closest(this.config.selectors.mute)) {
				this._toggleMute(e.target.closest(this.config.selectors.mute));
			} else if (e.target.closest(this.config.selectors.open)) {
				this._openModal(e.target.closest(this.config.selectors.open));
			} else if (e.target.closest(this.config.selectors.close)) {
				this._closeModal();
			}
		});

		document.addEventListener('keydown', (e) => {
			if (!this._activeModal) return;
			if (e.key === 'Escape') this._closeModal();
			if (e.key === 'Tab') this._trapFocus(e);
		});
	},

	_toggleMute(btn) {
		const root = btn.closest(this.config.selectors.root);
		if (!root) return;

		const nextMuted = btn.getAttribute('aria-pressed') !== 'true';
		root.querySelectorAll(this.config.selectors.video).forEach((v) => {
			v.muted = nextMuted;
		});

		btn.setAttribute('aria-pressed', String(nextMuted));
		btn.setAttribute(
			'aria-label',
			nextMuted ? btn.dataset.labelUnmute : btn.dataset.labelMute
		);
	},

	_openModal(trigger) {
		const scope =
			trigger.closest('.shopify-section') ||
			trigger.closest(this.config.selectors.root)?.parentElement ||
			document;
		const modal = scope.querySelector(this.config.selectors.modal);
		if (!modal) {
			window.log?.('warn', '🎬 [VIDEO_CONTENT]', 'Modal introuvable');
			return;
		}

		const video = modal.querySelector(this.config.selectors.fullscreen);

		this._trigger = trigger;
		this._activeModal = modal;
		modal.hidden = false;
		document.documentElement.style.overflow = 'hidden';

		if (video && typeof video.play === 'function') {
			video.currentTime = 0;
			video.muted = false;
			const playPromise = video.play();
			if (playPromise) playPromise.catch(() => {});
		}

		modal.querySelector(this.config.selectors.close)?.focus();
		window.log?.('info', '🎬 [VIDEO_CONTENT]', 'Modal opened');
	},

	_closeModal() {
		const modal = this._activeModal;
		if (!modal) return;

		const video = modal.querySelector(this.config.selectors.fullscreen);
		if (video && typeof video.pause === 'function') video.pause();
		modal.hidden = true;
		document.documentElement.style.overflow = '';

		this._trigger?.focus();
		this._trigger = null;
		this._activeModal = null;
	},

	_trapFocus(e) {
		const focusable = [
			...this._activeModal.querySelectorAll(this.config.selectors.focusable),
		].filter((el) => el.offsetParent !== null);
		if (!focusable.length) return;

		const first = focusable[0];
		const last = focusable[focusable.length - 1];

		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => MMVideoContentManager.init());
} else {
	MMVideoContentManager.init();
}

// Theme Editor : la section est re-rendue lors de l'édition
document.addEventListener('shopify:section:load', () => MMVideoContentManager.init());
